import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box, Typography, Button, TextField, Table, TableHead, TableBody,
  TableRow, TableCell, TableContainer, Paper, Chip, IconButton, TableSortLabel,
  CircularProgress, Snackbar, Alert, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions, InputAdornment, Tooltip,
} from '@mui/material';
import {
  Work as WorkIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  FileDownload as DownloadIcon,
} from '@mui/icons-material';
import apiClient from '../api/client';
import { Project } from '../types';
import ProjectFormDialog from '../components/admin/ProjectFormDialog';

type SortField = 'project_name' | 'client_name' | 'project_type' | 'status' | 'revenue';
type SortDir = 'asc' | 'desc';

const STATUS_COLORS: Record<string, 'success' | 'warning' | 'default' | 'error'> = {
  active: 'success',
  completed: 'default',
  on_hold: 'warning',
  cancelled: 'error',
};

const STATUS_LABELS: Record<string, string> = {
  active: 'Active',
  completed: 'Completed',
  on_hold: 'On Hold',
  cancelled: 'Cancelled',
};

export default function AdminProjectsPage() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<SortField>('project_name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // Dialogs
  const [formOpen, setFormOpen] = useState(false);
  const [editProject, setEditProject] = useState<Project | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Project | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/projects');
      setProjects(data);
    } catch (err) {
      console.error('Fetch projects error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSort = (field: SortField) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const filtered = useMemo(() => {
    let result = [...projects];
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(p =>
        p.project_name.toLowerCase().includes(q) ||
        p.client_name.toLowerCase().includes(q) ||
        p.project_type.toLowerCase().includes(q)
      );
    }
    result.sort((a, b) => {
      let av: string | number = a[sortField] ?? '';
      let bv: string | number = b[sortField] ?? '';
      if (sortField === 'revenue') { av = Number(av); bv = Number(bv); }
      else { av = String(av).toLowerCase(); bv = String(bv).toLowerCase(); }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return result;
  }, [projects, search, sortField, sortDir]);

  const totalRevenue = useMemo(
    () => projects.filter(p => p.status === 'active' || p.status === 'completed').reduce((sum, p) => sum + Number(p.revenue), 0),
    [projects]
  );

  const handleFormClose = () => { setFormOpen(false); setEditProject(null); };

  const handleSaved = (saved: Project) => {
    setProjects(prev => {
      const idx = prev.findIndex(p => p.id === saved.id);
      if (idx >= 0) { const next = [...prev]; next[idx] = saved; return next; }
      return [saved, ...prev];
    });
    setSnackbar({ message: editProject ? 'Project updated.' : 'Project created.', severity: 'success' });
    handleFormClose();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await apiClient.delete(`/projects/${deleteTarget.id}`);
      setProjects(prev => prev.filter(p => p.id !== deleteTarget.id));
      setSnackbar({ message: 'Project deleted.', severity: 'success' });
    } catch {
      setSnackbar({ message: 'Failed to delete project.', severity: 'error' });
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const handleDownloadSOW = async (project: Project) => {
    try {
      const response = await apiClient.get(`/projects/${project.id}/sow`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = project.sow_filename || 'SOW';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      setSnackbar({ message: 'No SOW file found for this project.', severity: 'error' });
    }
  };

  const SortCell = ({ field, label }: { field: SortField; label: string }) => (
    <TableCell sortDirection={sortField === field ? sortDir : false}
      sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em',
            bgcolor: '#F0F4FF', borderBottom: '2px solid #BFDBFE', whiteSpace: 'nowrap' }}>
      <TableSortLabel active={sortField === field} direction={sortField === field ? sortDir : 'asc'}
        onClick={() => handleSort(field)}>
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WorkIcon sx={{ color: 'primary.main' }} /> Projects
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Fixed-price engagements and contract revenue
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditProject(null); setFormOpen(true); }}
          sx={{ textTransform: 'none', fontWeight: 600 }}>
          Add Project
        </Button>
      </Box>

      {/* Summary card */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Paper variant="outlined" sx={{ px: 3, py: 2, borderLeft: '4px solid #8B5CF6', minWidth: 200 }}>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: 11 }}>
            Total Project Revenue
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#8B5CF6', mt: 0.5 }}>
            ${totalRevenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Typography>
          <Typography variant="caption" color="text.secondary">Active + Completed</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ px: 3, py: 2, borderLeft: '4px solid #3B82F6', minWidth: 160 }}>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: 11 }}>
            Total Projects
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#3B82F6', mt: 0.5 }}>
            {projects.length}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {projects.filter(p => p.status === 'active').length} active
          </Typography>
        </Paper>
      </Box>

      {/* Search */}
      <Box sx={{ mb: 2 }}>
        <TextField size="small" placeholder="Search projects, clients…" value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} /></InputAdornment> }}
          sx={{ width: 300 }} />
      </Box>

      {/* Table */}
      <Paper variant="outlined">
        <TableContainer>
          <Table size="small" sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <SortCell field="project_name" label="Project Name" />
                <SortCell field="client_name"  label="Client" />
                <SortCell field="project_type" label="Type" />
                <SortCell field="status"       label="Status" />
                <SortCell field="revenue"      label="Revenue" />
                <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em',
                  bgcolor: '#F0F4FF', borderBottom: '2px solid #BFDBFE', width: 120 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 6 }}><CircularProgress size={28} /></TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                  {search ? 'No projects match your search.' : 'No projects yet. Click "Add Project" to get started.'}
                </TableCell></TableRow>
              ) : filtered.map((p, i) => (
                <TableRow key={p.id} sx={{ bgcolor: i % 2 === 1 ? '#FAFBFD' : 'inherit', '&:hover': { bgcolor: '#F0F4FF' } }}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.project_name}</Typography>
                    {p.start_date && (
                      <Typography variant="caption" color="text.secondary">
                        {new Date(p.start_date).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}
                        {p.end_date && ` → ${new Date(p.end_date).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}`}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell><Typography variant="body2">{p.client_name}</Typography></TableCell>
                  <TableCell>
                    <Chip label={p.project_type} size="small" variant="outlined"
                      sx={{ fontSize: 11, height: 24, fontWeight: 600 }} />
                  </TableCell>
                  <TableCell>
                    <Chip label={STATUS_LABELS[p.status] || p.status}
                      color={STATUS_COLORS[p.status] || 'default'}
                      size="small" variant="outlined"
                      sx={{ fontSize: 11, height: 24, fontWeight: 600 }} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      ${Number(p.revenue).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title={p.sow_filename ? `Download ${p.sow_filename}` : 'No SOW uploaded'}>
                        <span>
                          <IconButton size="small" onClick={() => handleDownloadSOW(p)} disabled={!p.sow_filename}
                            sx={{ color: p.sow_filename ? '#8B5CF6' : 'text.disabled' }}>
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Edit project">
                        <IconButton size="small" onClick={() => { setEditProject(p); setFormOpen(true); }} sx={{ color: 'primary.main' }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete project">
                        <IconButton size="small" onClick={() => setDeleteTarget(p)} sx={{ color: 'error.main' }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit Dialog */}
      <ProjectFormDialog open={formOpen} onClose={handleFormClose} onSaved={handleSaved} project={editProject} />

      {/* Delete Confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1.5 }}>Delete Project</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to permanently delete <strong>{deleteTarget?.project_name}</strong>? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDeleteTarget(null)} color="inherit" disabled={deleting}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={!!snackbar} autoHideDuration={4000} onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {snackbar ? (
          <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar(null)}>
            {snackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
